import { useQuery } from '@tanstack/react-query';

import { TimeOffTypeCode } from '@/modules/common/types/time-off-type';

import { getMemberTimeOffBalances } from '../api/member-time-offs.api';
import { getMemberTimeOffBalancesQueryKey } from '../constants/member-time-offs-query-keys';

// Time-off type codes that must never surface in the member's balances/types.
// Public Holiday is filtered out because Opsium does not currently support that
// time-off type — there's no flow to manage or book it, so it shouldn't appear
// as a balance or in the time-off types list.
const UNSUPPORTED_TYPE_CODES: TimeOffTypeCode[] = [TimeOffTypeCode.PUBLIC_HOLIDAY];
// Balances change through mutations that invalidate/refetch the query,
// so short-lived navigation should reuse cache.
const MEMBER_TIME_OFF_BALANCES_STALE_TIME = 5 * 60 * 1000;

/**
 * Fetches a single team member's time-off balances. Disabled until a memberId
 * is available. Filters out unsupported time-off types (see
 * `UNSUPPORTED_TYPE_CODES`) at the hook level so every consumer (cards, compact
 * strip, details balance lookup) stays consistent.
 */
export const useMemberTimeOffBalances = (memberId: string | undefined) => {
  return useQuery({
    queryKey: getMemberTimeOffBalancesQueryKey(memberId ?? ''),
    queryFn: () => getMemberTimeOffBalances(memberId as string),
    enabled: !!memberId,
    select: balances => balances.filter(b => !UNSUPPORTED_TYPE_CODES.includes(b.type.code)),
    staleTime: MEMBER_TIME_OFF_BALANCES_STALE_TIME,
  });
};
